import { useState } from "react";
import { Link } from "react-router-dom";
import { Banknote, CreditCard, Facebook, Instagram, MapPin } from "lucide-react";
import { toast } from "sonner";
import { useShop, useI18n } from "@/context/Shop";
import { api, errMsg } from "@/lib/api";
import { CurrencyToggle, LanguageToggle } from "@/components/store/Toggles";

export function NewsletterForm({ source = "footer", dark = true }) {
  const { lang } = useShop();
  const { t } = useI18n();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setBusy(true);
    try {
      await api.post("/store/newsletter", { email, language: lang, source });
      setDone(true);
      toast.success(t("subscribed"));
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  if (done) return <p className={`text-sm ${dark ? "text-white/80" : "text-muted-foreground"}`} data-testid="footer-newsletter-success">{t("subscribed")}</p>;
  return (
    <form onSubmit={submit} className={`flex border ${dark ? "border-white/40" : "border-foreground/60"}`} data-testid="footer-newsletter-form">
      <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t("email_placeholder")} aria-label={t("email")}
        className={`flex-1 min-w-0 h-12 px-4 bg-transparent text-sm outline-none ${dark ? "text-white placeholder:text-white/50" : "placeholder:text-muted-foreground"}`} data-testid="footer-newsletter-input" />
      <button type="submit" disabled={busy} className={`h-12 px-5 text-[11px] uppercase tracking-[0.16em] font-semibold transition-colors ${dark ? "bg-white text-black hover:bg-white/90" : "bg-foreground text-background hover:bg-foreground/90"} disabled:opacity-50`} data-testid="footer-newsletter-submit">{t("subscribe")}</button>
    </form>
  );
}

export function Footer() {
  const { config } = useShop();
  const { t } = useI18n();
  const name = config?.store_name || "EA Italian Luxury Brands";
  const cols = [
    { title: t("customer_care"), id: "care", links: [[t("track_order"), "/customer-care/track-order"], [t("contact"), "/customer-care/contact"], [t("payment"), "/customer-care/payment"], [t("delivery"), "/customer-care/delivery"], [t("returns"), "/customer-care/returns"], [t("faq"), "/customer-care/faq"], [t("size_guides"), "/customer-care/size-guides"]] },
    { title: t("discover"), id: "shop", links: [[t("new_arrivals"), "/new-arrivals"], [t("women"), "/women"], [t("men"), "/men"], [t("kids"), "/kids"], [t("brands"), "/brands"], [t("collections"), "/collections"], [t("sale"), "/sale"]] },
    { title: t("about_us"), id: "about", links: [[t("about"), "/about"], [t("stores"), "/customer-care/contact"], [t("my_account"), "/account"], [t("wishlist"), "/wishlist"]] },
    { title: t("legal"), id: "legal", links: [[t("terms"), "/customer-care/terms"], [t("privacy"), "/customer-care/privacy"], [t("cookies"), "/customer-care/cookies"]] },
  ];
  return (
    <footer className="mt-24 bg-[hsl(var(--header-bg))] text-[hsl(var(--header-fg))]" data-testid="footer">
      <div className="container-lux py-14 grid lg:grid-cols-12 gap-10 border-b border-white/10">
        <div className="lg:col-span-5">
          <p className="font-display text-3xl leading-tight">{t("newsletter")}</p>
          <p className="text-sm text-white/70 mt-3 max-w-md leading-relaxed">{t("newsletter_sub")}</p>
        </div>
        <div className="lg:col-span-6 lg:col-start-7 self-center"><NewsletterForm /></div>
      </div>
      <div className="container-lux py-14 grid grid-cols-2 md:grid-cols-4 gap-10">
        {cols.map((c) => (
          <div key={c.id}>
            <p className="text-[11px] uppercase tracking-[0.18em] font-semibold text-white mb-5">{c.title}</p>
            <ul className="space-y-2.5">
              {c.links.map(([l, to]) => <li key={to + l}><Link to={to} className="text-[13px] text-white/70 hover:text-white transition-colors" data-testid={`footer-link-${to.replace(/\//g, "-").slice(1)}`}>{l}</Link></li>)}
            </ul>
          </div>
        ))}
      </div>
      <div className="container-lux pb-10 grid md:grid-cols-12 gap-8 items-start">
        <div className="md:col-span-5">
          <p className="wordmark text-white">{name}</p>
          <p className="text-[13px] text-white/60 mt-4 leading-relaxed max-w-sm flex items-start gap-2"><MapPin className="h-4 w-4 mt-0.5 shrink-0" strokeWidth={1.5} /><span>{config?.store_address || "Tiranë, Albania"}<br />{config?.opening_hours}</span></p>
          {config?.demo_catalog && <p className="mt-5 inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.14em] border border-white/30 text-white/80 px-2.5 py-1" data-testid="footer-demo-badge">{t("demo")} · {t("demo_banner")}</p>}
        </div>
        <div className="md:col-span-4">
          <p className="text-[11px] uppercase tracking-[0.18em] font-semibold text-white mb-4">{t("payment_methods")}</p>
          <div className="flex flex-wrap gap-2" data-testid="footer-payment-methods">
            <span className="inline-flex items-center gap-1.5 border border-white/30 px-2.5 h-8 text-[11px] font-semibold tracking-[0.08em]"><CreditCard className="h-3.5 w-3.5" strokeWidth={1.6} />VISA</span>
            <span className="inline-flex items-center gap-1.5 border border-white/30 px-2.5 h-8 text-[11px] font-semibold tracking-[0.08em]"><CreditCard className="h-3.5 w-3.5" strokeWidth={1.6} />Mastercard</span>
            <span className="inline-flex items-center gap-1.5 border border-white/30 px-2.5 h-8 text-[11px] font-semibold tracking-[0.08em]"><Banknote className="h-3.5 w-3.5" strokeWidth={1.6} />{t("cod")}</span>
          </div>
          <p className="text-[11px] text-white/50 mt-3">{t("checkout_in_all")}</p>
        </div>
        <div className="md:col-span-3">
          <p className="text-[11px] uppercase tracking-[0.18em] font-semibold text-white mb-4">{t("follow")}</p>
          <div className="flex items-center gap-3">
            {config?.social?.instagram && <a href={config.social.instagram} target="_blank" rel="noreferrer" aria-label="Instagram" className="p-2 border border-white/30 hover:bg-white hover:text-black transition-colors" data-testid="footer-instagram-link"><Instagram className="h-4 w-4" strokeWidth={1.6} /></a>}
            {config?.social?.facebook && <a href={config.social.facebook} target="_blank" rel="noreferrer" aria-label="Facebook" className="p-2 border border-white/30 hover:bg-white hover:text-black transition-colors" data-testid="footer-facebook-link"><Facebook className="h-4 w-4" strokeWidth={1.6} /></a>}
          </div>
          <div className="mt-6 space-y-2">
            <LanguageToggle />
            <CurrencyToggle />
          </div>
        </div>
      </div>
      <div className="border-t border-white/10">
        <div className="container-lux py-5 flex flex-col sm:flex-row justify-between gap-2 text-[11px] text-white/50">
          <span>© {new Date().getFullYear()} {name}. {t("rights")}</span>
          <Link to="/admin" className="hover:text-white transition-colors" data-testid="footer-staff-link">{t("staff")}</Link>
        </div>
      </div>
    </footer>
  );
}
